import type {
  $ZodDefault,
  $ZodNullable,
  $ZodShape,
  FieldDescriptions,
  ZodObject,
  ZodReadonly,
  ZodType,
  output,
} from "@core/zod";
import { z } from "@core/zod";

import type { ReadonlyRecord } from "../types/index.ts";

export interface ModelSchemas<
  Schema extends ZodType,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
> {
  readonly createSchema: CreateSchema;
  readonly readSchema: ReadSchema;
  readonly schema: Schema;
  readonly updateSchema: UpdateSchema;
}

type ModelShape<Schema extends ZodType> =
  Schema extends ZodReadonly<ZodObject<infer S>> ? S : never;

type ModelDescriptions<Schema extends ZodType> =
  Schema extends ZodReadonly<ZodObject<infer S>> ? FieldDescriptions<S> : never;

export interface Model<
  Schema extends ZodType,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
> extends ModelSchemas<Schema, ReadSchema, CreateSchema, UpdateSchema> {
  readonly create: (item: z.input<CreateSchema>) => z.infer<Schema>;
  readonly descriptions: ModelDescriptions<Schema>;
  readonly shape: ModelShape<Schema>;
  readonly update: (
    item: z.infer<Schema>,
    update: z.input<UpdateSchema>,
  ) => z.infer<Schema>;
}

interface CreateModelParams<
  Schema extends ZodType,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
> {
  readonly create: (
    item: z.input<CreateSchema>,
    schema: CreateSchema,
  ) => z.infer<Schema>;
  readonly schemas: ModelSchemas<
    Schema,
    ReadSchema,
    CreateSchema,
    UpdateSchema
  >;
  readonly update: (
    item: z.infer<Schema>,
    update: z.input<UpdateSchema>,
    schema: UpdateSchema,
  ) => z.infer<Schema>;
}

export function createModel<
  Schema extends ZodType,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
  Extension extends object,
>(
  {
    create,
    schemas: { createSchema, readSchema, schema, updateSchema },
    update,
  }: CreateModelParams<Schema, ReadSchema, CreateSchema, UpdateSchema>,
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  extension: Extension = {} as Extension,
): Extension & Model<Schema, ReadSchema, CreateSchema, UpdateSchema> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  const object = schema as unknown as ZodReadonly<ZodObject>;

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  const descriptions = object.descriptions as ModelDescriptions<Schema>;

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  const shape = object.shape as ModelShape<Schema>;

  return {
    create: item => create(item, createSchema),
    createSchema,
    descriptions,
    readSchema,
    schema,
    shape,
    update: (item, itemUpdate) => update(item, itemUpdate, updateSchema),
    updateSchema,
    ...extension,
  };
}

interface CreateModelIdParams<P extends string> {
  readonly digestFn: () => string;
  readonly idPrefix: P;
}

interface CreateModelIdResult<P extends string> {
  readonly generateDeterministicId: (digest: string) => `${P}${string}`;
  readonly generateId: () => `${P}${string}`;
  readonly getDigestFromId: (id: `${P}${string}`) => string;
  readonly idSchema: ZodType<`${P}${string}`, `${P}${string}`>;
}

export function createModelId<P extends string>({
  digestFn,
  idPrefix,
}: CreateModelIdParams<P>): CreateModelIdResult<P> {
  return {
    generateDeterministicId: digest => `${idPrefix}${digest}`,
    generateId: () => `${idPrefix}${digestFn()}`,
    getDigestFromId: id => id.slice(idPrefix.length),
    idSchema: z.safeId().startsWith(idPrefix),
  };
}

type ModelDefaults<Shape extends $ZodShape> = {
  readonly [K in keyof Shape as undefined extends output<Shape[K]>
    ? never
    : K]?: output<Shape[K]>;
};

type ModelShapeWithDefaults<
  Shape extends $ZodShape,
  Defaults extends ModelDefaults<Shape>,
> = {
  readonly [K in keyof Shape]: K extends keyof Defaults
    ? $ZodDefault<Shape[K]>
    : Shape[K] extends $ZodNullable
      ? $ZodDefault<Shape[K]>
      : Shape[K];
};

type ModelSchemaWithDefaults<
  Shape extends $ZodShape,
  Defaults extends ModelDefaults<Shape>,
> = ZodReadonly<ZodObject<ModelShapeWithDefaults<Shape, Defaults>>>;

interface ModelSchemaDerivations<
  Shape extends $ZodShape,
  Defaults extends ModelDefaults<Shape>,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
> {
  readonly create: (
    schema: ModelSchemaWithDefaults<Shape, Defaults>,
  ) => CreateSchema;
  readonly recovery: (
    schema: ModelSchemaWithDefaults<Shape, Defaults>,
    sources: { readonly base: ZodReadonly<ZodObject<Shape>> },
  ) => ReadSchema;
  readonly update: (schema: ZodReadonly<ZodObject<Shape>>) => UpdateSchema;
}

interface CreateModelSchemasParams<
  Shape extends $ZodShape,
  Defaults extends ModelDefaults<Shape>,
  ReadSchema extends ZodType,
  CreateSchema extends ZodType,
  UpdateSchema extends ZodType,
> {
  readonly base: ZodReadonly<ZodObject<Shape>>;
  readonly defaults?: Defaults;
  readonly derive: ModelSchemaDerivations<
    Shape,
    Defaults,
    ReadSchema,
    CreateSchema,
    UpdateSchema
  >;
}

export function createModelSchemas<
  Shape extends $ZodShape,
  const Defaults extends ModelDefaults<Shape> = {},
  ReadSchema extends ZodType = ZodType,
  CreateSchema extends ZodType = ZodType,
  UpdateSchema extends ZodType = ZodType,
>({
  base: schema,
  defaults,
  derive,
}: CreateModelSchemasParams<
  Shape,
  Defaults,
  ReadSchema,
  CreateSchema,
  UpdateSchema
>): ModelSchemas<
  ZodReadonly<ZodObject<Shape>>,
  ReadSchema,
  CreateSchema,
  UpdateSchema
> {
  const defaultByKey: ReadonlyRecord<string, unknown> = defaults ?? {};
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  const schemaWithDefaults = mapFields(schema, (field, key) => {
    if (Object.hasOwn(defaultByKey, key)) {
      return field.default(defaultByKey[key]);
    }

    return field instanceof z.ZodNullable ? field.default(null) : field;
  }) as unknown as ModelSchemaWithDefaults<Shape, Defaults>;

  return {
    createSchema: derive.create(schemaWithDefaults),
    readSchema: derive.recovery(schemaWithDefaults, { base: schema }),
    schema,
    updateSchema: derive.update(schema),
  };
}

export function noModelUpdate(): ZodReadonly<ZodObject<{}>> {
  return z.object({});
}

function mapFields(
  schema: ZodReadonly<ZodObject>,
  mapField: (field: ZodType, key: string) => ZodType,
): ZodReadonly<ZodObject> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  const shape = schema.shape as ReadonlyRecord<string, ZodType>;
  const fields = Object.entries(shape).map(
    ([key, field]) => [key, mapField(field, key)] as const,
  );

  return schema.extend(Object.fromEntries(fields), schema.descriptions);
}
